import type { PublicEventParamType, PublicEventPreset, PublicEventVisibilityRule } from "../../EntityPresetEditor/publicEventPresets";
import { getPublicEventParameterDefault } from "../../EntityPresetEditor/publicEventPresets";
import type { PerformanceClip } from "../types/DialogueNode";
import type { DialogueProject } from "../types/FileStruct";
import { createClipComponent } from "../config/clipComponentRegistry";
import { systemPresetConfig } from "../../EntityPresetEditor/systemPresetConfig";
export interface PublicEventArgument { id: string; name: string; type: PublicEventParamType; value: string; reference?: string; visibleWhen?: PublicEventVisibilityRule[] }
/** 为已保存的实体朝向动作提供新实体候选，不改写其参数值或显式来源。 */
export function getPublicEventArgumentReference(clip: PerformanceClip, parameter: PublicEventArgument): string | undefined {
  // 旧布尔来源已移除；保留参数的类型和值，改为对应类型的直接输入。
  if (parameter.reference === "booleans.value") return undefined;
  if (parameter.reference) return parameter.reference;
  const properties = clip.components.find(item => item.templateId === "public.event")?.properties;
  const preset = systemPresetConfig.publicEvents.presets.find(item => item.id === "public-set-object-entity-forward");
  if (!preset || properties?.value !== preset.name) return undefined;
  const reference = preset.parameters.find(item => item.id === parameter.id && item.type === parameter.type)?.reference;
  return reference === "entities.guid" || reference === "entities.entityQuery" ? reference : undefined;
}
export function isPublicEventArgumentVisible(parameter: PublicEventArgument, parameters: PublicEventArgument[]): boolean {
  const rules = parameter.visibleWhen;
  if (!rules?.length) return true;
  // 缺失或损坏的条件保持显示，避免隐藏需要修正的数据。
  if (!Array.isArray(rules) || rules.some(rule => !rule || !parameters.some(item => item?.id === rule.parameterId))) return true;
  return rules.every(rule => {
    const source = parameters.find(item => item.id === rule.parameterId)!;
    return source.type === "Int32"
      ? /^[+-]?\d+$/.test(source.value.trim()) && /^[+-]?\d+$/.test(rule.equals.trim()) && Number(source.value) === Number(rule.equals)
      : source.value === rule.equals;
  });
}
export function getPublicEventArguments(clip: PerformanceClip): PublicEventArgument[] {
  const parameters = clip.components.find(item => item.templateId === "public.event")?.properties.parameters;
  return Array.isArray(parameters) ? parameters : [];
}
/** 编辑器显示规则跟随当前预设；动作的值、事件名和参数结构仍保留创建时的快照。 */
export function syncPublicEventVisibility(clip: PerformanceClip, presets: PublicEventPreset[]): boolean {
  const properties = clip.components.find(item => item.templateId === "public.event")?.properties;
  const preset = presets.find(item => item.id === properties?.presetId);
  if (!properties || !preset) return false;
  let changed = false;
  const requiresDuration = preset.requiresDuration ?? systemPresetConfig.publicEvents.newItem.requiresDuration;
  if ((properties.requiresDuration !== false) !== requiresDuration) {
    properties.requiresDuration = requiresDuration;
    changed = true;
  }
  for (const parameter of getPublicEventArguments(clip)) {
    const definition = preset.parameters.find(item => item.id === parameter.id && item.type === parameter.type);
    if (!definition || JSON.stringify(parameter.visibleWhen ?? []) === JSON.stringify(definition.visibleWhen ?? [])) continue;
    // 旧 Int32 开关保持 0/1 条件，不能随新版 Bool 开关改成 True/False。
    const rules = [...(parameter.visibleWhen ?? []), ...(definition.visibleWhen ?? [])];
    if (rules.some(rule => {
      const savedSource = getPublicEventArguments(clip).find(item => item.id === rule.parameterId);
      const currentSource = preset.parameters.find(item => item.id === rule.parameterId);
      return savedSource && currentSource && savedSource.type !== currentSource.type;
    })) continue;
    if (definition.visibleWhen?.length) parameter.visibleWhen = definition.visibleWhen.map(rule => ({ ...rule }));
    else delete parameter.visibleWhen;
    changed = true;
  }
  return changed;
}
/** 包括尚未打开参数面板的公共事件，确保显示、保存与导出使用同一份条件。 */
export function syncDialoguePublicEventVisibility(project: DialogueProject, presets: PublicEventPreset[]): boolean {
  let changed = false;
  for (const node of Object.values(project.dialogue.nodes)) {
    for (const line of node.lines) {
      for (const clip of line.clips) {
        if (syncPublicEventVisibility(clip, presets)) changed = true;
      }
    }
  }
  return changed;
}
export function getPublicEventClipLabel(clip: PerformanceClip, presets: PublicEventPreset[]): string {
  const properties = clip.components.find(item => item.templateId === "public.event")?.properties;
  const preset = presets.find(item => item.id === properties?.presetId)
    ?? presets.find(item => item.name === properties?.value);
  if (preset?.alias.trim()) return preset.alias.trim();
  const savedAlias = properties?.presetAlias;
  return typeof savedAlias === "string" && savedAlias.trim() ? savedAlias.trim() : "公共事件";
}
export function applyPublicEventPreset(clip: PerformanceClip, preset: PublicEventPreset) {
  let config = clip.components.find(item => item.templateId === "public.event");
  if (!config) { config = createClipComponent("public.event"); clip.components.push(config); }
  const previous = config.properties.presetId === preset.id ? getPublicEventArguments(clip) : [];
  config.enabled = true;
  config.properties = { ...config.properties, value: preset.name, presetId: preset.id, presetAlias: preset.alias,
    requiresDuration: preset.requiresDuration ?? systemPresetConfig.publicEvents.newItem.requiresDuration,
    parameters: preset.parameters.map(param => {
      const value = previous.find(item => item.id === param.id && item.type === param.type)?.value ?? param.defaultValue;
      return { ...(param.visibleWhen ? { visibleWhen: param.visibleWhen.map(rule => ({ ...rule })) } : {}), ...(param.reference ? { reference: param.reference } : {}), id: param.id, name: param.name, type: param.type,
        value: param.type === "Bool" && !value ? getPublicEventParameterDefault(param.type) : value,
      };
    }),
  };
}
export function compilePublicEventArguments(eventName: string, parameters: unknown) {
  const lists = { stringParams: [eventName], intParams: [] as string[], guidParams: [] as string[], configParams: [] as string[], prefabParams: [] as string[], floatParams: [] as string[], boolParams: [] as string[] };
  const fields = { String: "stringParams", Int32: "intParams", Guid: "guidParams", ConfigReference: "configParams", EntityReference: "prefabParams", Float: "floatParams", Bool: "boolParams" } as const;
  if (parameters === undefined) return lists;
  if (!Array.isArray(parameters)) throw new Error("公共事件参数必须是列表。");
  parameters.forEach((parameter, index) => {
    const fail = () => new Error(`公共事件「${eventName}」参数「${parameter?.name || index + 1}」不符合 ${parameter?.type || "未知"} 类型。`);
    if (!parameter || typeof parameter.value !== "string") throw fail();
    const type = parameter.type as PublicEventParamType;
    if (!Object.prototype.hasOwnProperty.call(fields, type)) throw fail();
    let value = isPublicEventArgumentVisible(parameter, parameters) ? parameter.value : type === "String" ? "" : type === "Bool" ? "False" : "0";
    if (type !== "String") {
      value = value.trim();
      if (type === "Bool") {
        if (value !== "True" && value !== "False") throw fail();
      } else if (type === "Float") {
        if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/.test(value) || !Number.isFinite(Number(value)) || !Number.isFinite(Math.fround(Number(value)))) throw fail();
        value = String(Number(value));
      } else if (type === "Int32") {
        if (!/^[+-]?\d+$/.test(value) || !Number.isInteger(Number(value)) || Number(value) < -2147483648 || Number(value) > 2147483647) throw fail();
        value = String(Number(value));
      } else {
        if (!/^\d+$/.test(value)) throw fail();
        // GUID 保留数字文本，不经过 JS Number，避免大整数精度丢失。
        if (type !== "Guid" && (!Number.isSafeInteger(Number(value)) || Number(value) > 2147483647)) throw fail();
      }
    }
    lists[fields[type]].push(value);
  });
  for (const [field, values] of Object.entries(lists)) {
    if (values.length > 100) throw new Error(`公共事件「${eventName}」的 ${field} 最多 100 项（字符串列表包含事件名）。`);
  }
  return lists;
}
